import React, { useRef, useState, useEffect, useLayoutEffect, useCallback, Children, isValidElement } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import 'components/ui/popover.scss';

const PLACEMENTS = ['top', 'bottom', 'left', 'right', 'top-start', 'top-end', 'bottom-start', 'bottom-end'];
const TRIGGERS = ['hover', 'click', 'focus'];

const resolveTarget = (target) => {
  if (!target) return null;
  if (typeof target === 'string') return document.getElementById(target);
  if (target.current) return target.current;
  if (target instanceof Element) return target;
  return null;
};

const resolveContainer = (container) => {
  if (!container) return document.body;
  if (typeof container === 'string') return document.querySelector(container) || document.body;
  if (container.current) return container.current;
  if (container instanceof Element) return container;
  return document.body;
};

const computePosition = (triggerRect, popRect, placement, offset) => {
  const margin = offset;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let top;
  let left;

  if (placement.startsWith('top')) {
    top = triggerRect.top - popRect.height - margin;
    if (top < margin) top = triggerRect.bottom + margin;
  } else if (placement.startsWith('bottom')) {
    top = triggerRect.bottom + margin;
    if (top + popRect.height > vh - margin) top = triggerRect.top - popRect.height - margin;
  } else if (placement === 'left') {
    top = triggerRect.top + (triggerRect.height - popRect.height) / 2;
  } else if (placement === 'right') {
    top = triggerRect.top + (triggerRect.height - popRect.height) / 2;
  }

  if (placement === 'left') {
    left = triggerRect.left - popRect.width - margin;
    if (left < margin) left = triggerRect.right + margin;
  } else if (placement === 'right') {
    left = triggerRect.right + margin;
    if (left + popRect.width > vw - margin) left = triggerRect.left - popRect.width - margin;
  } else if (placement.endsWith('-start')) {
    left = triggerRect.left;
  } else if (placement.endsWith('-end')) {
    left = triggerRect.right - popRect.width;
  } else {
    left = triggerRect.left + (triggerRect.width - popRect.width) / 2;
  }

  top = Math.max(margin, Math.min(top, vh - popRect.height - margin));
  left = Math.max(margin, Math.min(left, vw - popRect.width - margin));

  return { top, left };
};

const Popover = ({
  target,
  trigger = 'click',
  placement = 'top',
  isOpen: isOpenProp,
  defaultOpen = false,
  onToggle,
  container,
  className,
  offset = 8,
  delay = 0,
  closeOnClickOutside = true,
  closeOnEscape = true,
  showArrow = true,
  width,
  children,
  'data-cy': dataCy,
}) => {
  const isControlled = isOpenProp !== undefined;
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isOpen = isControlled ? isOpenProp : internalOpen;

  const popRef = useRef(null);
  const targetElRef = useRef(null);
  const openTimeoutRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  const [position, setPosition] = useState(null);

  const setOpen = useCallback(
    (next) => {
      if (!isControlled) setInternalOpen(next);
      if (onToggle) onToggle(next);
    },
    [isControlled, onToggle],
  );

  const clearTimers = () => {
    if (openTimeoutRef.current) clearTimeout(openTimeoutRef.current);
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    openTimeoutRef.current = null;
    closeTimeoutRef.current = null;
  };

  const open = useCallback(() => {
    clearTimers();
    if (delay > 0) {
      openTimeoutRef.current = setTimeout(() => setOpen(true), delay);
    } else {
      setOpen(true);
    }
  }, [delay, setOpen]);

  const close = useCallback(() => {
    clearTimers();
    setOpen(false);
  }, [setOpen]);

  const toggle = useCallback(() => {
    setOpen(!isOpen);
  }, [isOpen, setOpen]);

  // Stable refs to handlers so the bind-events effect doesn't re-run on every state change
  const handlersRef = useRef({});
  handlersRef.current.open = open;
  handlersRef.current.close = close;
  handlersRef.current.toggle = toggle;

  // Resolve and bind events on the target element
  useEffect(() => {
    if (isControlled) {
      targetElRef.current = resolveTarget(target);
      return undefined;
    }
    const el = resolveTarget(target);
    targetElRef.current = el;
    if (!el) return undefined;

    if (trigger === 'click') {
      const onClick = () => handlersRef.current.toggle();
      el.addEventListener('click', onClick);
      return () => {
        el.removeEventListener('click', onClick);
      };
    }
    if (trigger === 'hover') {
      const onEnter = () => handlersRef.current.open();
      const onLeave = () => {
        closeTimeoutRef.current = setTimeout(() => handlersRef.current.close(), 200);
      };
      el.addEventListener('mouseenter', onEnter);
      el.addEventListener('mouseleave', onLeave);
      el.addEventListener('focus', onEnter);
      el.addEventListener('blur', onLeave);
      return () => {
        el.removeEventListener('mouseenter', onEnter);
        el.removeEventListener('mouseleave', onLeave);
        el.removeEventListener('focus', onEnter);
        el.removeEventListener('blur', onLeave);
      };
    }
    if (trigger === 'focus') {
      const onEnter = () => handlersRef.current.open();
      const onLeave = () => handlersRef.current.close();
      el.addEventListener('focus', onEnter);
      el.addEventListener('blur', onLeave);
      return () => {
        el.removeEventListener('focus', onEnter);
        el.removeEventListener('blur', onLeave);
      };
    }
    return undefined;
  }, [target, trigger, isControlled]);

  // Refresh target ref when controlled
  useEffect(() => {
    if (isControlled) targetElRef.current = resolveTarget(target);
  }, [target, isControlled, isOpen]);

  // Position the popover
  const reposition = useCallback(() => {
    const targetEl = targetElRef.current || resolveTarget(target);
    if (targetEl) targetElRef.current = targetEl;
    const popEl = popRef.current;
    if (!targetEl || !popEl) return;
    const triggerRect = targetEl.getBoundingClientRect();
    const popRect = popEl.getBoundingClientRect();
    setPosition(computePosition(triggerRect, popRect, placement, offset));
  }, [target, placement, offset]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return undefined;
    }
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [isOpen, reposition]);

  // Click outside / escape
  useEffect(() => {
    if (!isOpen) return undefined;
    const onMouseDown = (e) => {
      if (!closeOnClickOutside) return;
      if (trigger !== 'click') return;
      const popEl = popRef.current;
      const triggerEl = targetElRef.current;
      if (popEl && popEl.contains(e.target)) return;
      if (triggerEl && triggerEl.contains(e.target)) return;
      close();
    };
    const onKey = (e) => {
      if (closeOnEscape && e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen, trigger, closeOnClickOutside, closeOnEscape, close]);

  // Cancel close timer when cursor enters popover (hover trigger)
  const handlePopMouseEnter = () => {
    if (trigger === 'hover' && closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };
  const handlePopMouseLeave = () => {
    if (trigger === 'hover') {
      closeTimeoutRef.current = setTimeout(close, 200);
    }
  };

  useEffect(() => clearTimers, []);

  // Detect arrow side for visual rendering
  const arrowSide = placement.split('-')[0];

  // Detect if children include a PopoverHeader to apply spacing
  const hasHeader = Children.toArray(children).some((c) => isValidElement(c) && c.type && c.type.displayName === 'PopoverHeader');

  return ReactDOM.createPortal(
    <div
      ref={popRef}
      className={classNames('ui-popover', `ui-popover--${arrowSide}`, className, { 'ui-popover--with-header': hasHeader })}
      style={{
        display: isOpen ? 'block' : 'none',
        top: position ? position.top : 0,
        left: position ? position.left : 0,
        width: width || undefined,
      }}
      onMouseEnter={handlePopMouseEnter}
      onMouseLeave={handlePopMouseLeave}
      role="dialog"
      data-cy={dataCy}
    >
      {showArrow && <span className={`ui-popover-arrow ui-popover-arrow--${arrowSide}`} />}
      {isOpen && children}
    </div>,
    resolveContainer(container),
  );
};

Popover.propTypes = {
  target: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({ current: PropTypes.any }), PropTypes.instanceOf(Element)]).isRequired,
  trigger: PropTypes.oneOf(TRIGGERS),
  placement: PropTypes.oneOf(PLACEMENTS),
  isOpen: PropTypes.bool,
  defaultOpen: PropTypes.bool,
  onToggle: PropTypes.func,
  container: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({ current: PropTypes.any }), PropTypes.instanceOf(Element)]),
  className: PropTypes.string,
  offset: PropTypes.number,
  delay: PropTypes.number,
  closeOnClickOutside: PropTypes.bool,
  closeOnEscape: PropTypes.bool,
  showArrow: PropTypes.bool,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  children: PropTypes.node,
  'data-cy': PropTypes.string,
};

export const PopoverHeader = ({ children, className }) => (
  <div className={classNames('ui-popover-header', className)}>{children}</div>
);
PopoverHeader.displayName = 'PopoverHeader';
PopoverHeader.propTypes = { children: PropTypes.node, className: PropTypes.string };

export const PopoverBody = ({ children, className, style }) => (
  <div className={classNames('ui-popover-body', className)} style={style}>{children}</div>
);
PopoverBody.displayName = 'PopoverBody';
PopoverBody.propTypes = { children: PropTypes.node, className: PropTypes.string, style: PropTypes.object };

export default Popover;
